import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@1trading/react/components/ui/form';
import { RadioGroup, RadioGroupItem, RadioGroupItemCard } from '@1trading/react/components/ui/radio-group';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useGuardedFormStepper } from '@/components/ui/guarded-form-stepper';
import { PageHeading, PageHeadingDescription, PageHeadingTitle } from '@/components/ui/page-heading';
import { NONE_VALUE } from '@/constants/answers';
import type { AppropriatenessTestAverageComplexTransactionValue, AppropriatenessTestComplexTransactionCount } from '@/features/futures/schemas/appropriateness-test';
import { usePromise } from '@/hooks/use-promise';
import { AVERAGE_COMPLEX_TRANSACTION_VALUE_NOT_TRADED, FUTURES_APPROPRIATENESS_COMPLEX_TRANSACTION_COUNT_ANSWERS } from '@/services/api/profile/futures-appropriateness';

const FuturesAppropriatenessTestFieldsetComplexTransactionCountLegend = () => {
  const { t } = useTranslation();

  return (
    <PageHeading asChild>
      <legend>
        <PageHeadingTitle>{t('FuturesAppropriatenessAssessment.complexTransactionCount.title')}</PageHeadingTitle>
        <PageHeadingDescription>{t('FuturesAppropriatenessAssessment.complexTransactionCount.description')}</PageHeadingDescription>
      </legend>
    </PageHeading>
  );
};
FuturesAppropriatenessTestFieldsetComplexTransactionCountLegend.displayName = 'FuturesAppropriatenessTestFieldsetComplexTransactionCountLegend';

const FuturesAppropriatenessTestFieldsetComplexTransactionCount = () => {
  const form = useFormContext<Partial<AppropriatenessTestComplexTransactionCount & AppropriatenessTestAverageComplexTransactionValue>>();
  const { nextStepDeferred } = useGuardedFormStepper();
  const { isPending, handler: handleNextStep } = usePromise(() => nextStepDeferred({ submit: true }));
  const { t } = useTranslation();

  return (
    <FormField
      name="complexTransactionCount"
      control={form.control}
      render={({ field }) => (
        <FormItem>
          <FormLabel className="sr-only">{t('FuturesAppropriatenessAssessment.complexTransactionCount.title')}</FormLabel>
          <FormControl>
            <RadioGroup
              className="!mt-0"
              defaultValue={field.value}
              variant="cards"
              aria-label={t('FuturesAppropriatenessAssessment.complexTransactionCount.title')}
              onValueChange={(value) => {
                field.onChange(value);
                if (value === NONE_VALUE) form.setValue('averageComplexTransactionValue', AVERAGE_COMPLEX_TRANSACTION_VALUE_NOT_TRADED);
                handleNextStep();
              }}
            >
              {FUTURES_APPROPRIATENESS_COMPLEX_TRANSACTION_COUNT_ANSWERS.map((answer) => (
                <FormItem key={answer}>
                  <RadioGroupItemCard variant="outline">
                    <FormControl>
                      <RadioGroupItem value={answer} aria-disabled={isPending} />
                    </FormControl>
                    <FormLabel contained onClick={handleNextStep}>
                      {t(`FuturesAppropriatenessAssessment.complexTransactionCount.options.${answer}`)}
                    </FormLabel>
                  </RadioGroupItemCard>
                </FormItem>
              ))}
            </RadioGroup>
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};
FuturesAppropriatenessTestFieldsetComplexTransactionCount.displayName = 'FuturesAppropriatenessTestFieldsetComplexTransactionCount';
FuturesAppropriatenessTestFieldsetComplexTransactionCount.isAutoNextStep = true;

export { FuturesAppropriatenessTestFieldsetComplexTransactionCount, FuturesAppropriatenessTestFieldsetComplexTransactionCountLegend };
